import { useMemo } from "react"
import { useSelector } from "react-redux"
import { Plus, Trash2 } from "lucide-react"
import { Role, getRoleCode } from "constant/Role"
import { useGetActiveBudgetCategoriesQuery } from "shared/redux/endpoints/procurementV2"
import { useGetProcurementCentersQuery, useGetItemsQuery } from "shared/redux/endpoints/procurementItems"
import { useGetDepartmentsQuery } from "shared/redux/endpoints/procurement"
import { REGION_OPTIONS, CUSTOM_ITEM_VALUE } from "../../../constants"
import { Card, Button, Input, NumberInput, Select, SearchSelect, Textarea, Currency } from "../../../ui"
import { newNonPoLine, nonPoSpendTotal } from "./paymentRequestModel"

// GET /v1/departments comes back as the raw `{ data }` envelope (no
// transformResponse), and the list may sit one level deeper than the array.
// Rows are teams: { teamId, teamName, teamCode, region }.
export const departmentList = (raw) => {
  let node = raw
  for (let i = 0; i < 3 && node && !Array.isArray(node); i += 1) {
    node = node.data ?? node.content ?? node.items
  }
  return Array.isArray(node) ? node : []
}

// Non-PO (direct spend) form for the Payment create page: header fields plus
// lines that carry only a center, an item and the line total. Nothing here
// links to a vendor. Controlled — the parent owns `value` (emptyNonPoSpend).
const NonPoPaymentBuilder = ({ value, onChange, disabled }) => {
  const patch = (fields) => onChange({ ...value, ...fields })

  const { data: categories, isFetching: categoriesLoading } = useGetActiveBudgetCategoriesQuery({
    isActive: true,
    mustHavePurchase: false,
  })
  const categoryOptions = useMemo(
    () => (categories || []).map((c) => ({ value: c.id, label: c.name })),
    [categories]
  )

  // "Central" isn't a real hub.center_region bucket, so it doesn't filter.
  const { data: centersPage } = useGetProcurementCentersQuery({
    region: value.region === "North" || value.region === "South" ? value.region : undefined,
    size: 200,
  })
  // Same scoping as the order form: Finance and Procurement Manager pick any
  // center; everyone else only the centers they manage (empty = unscoped).
  const authCenterIds = useSelector((state) => state.auth.centerIds)
  const activeRoleCode = useSelector((state) => getRoleCode(state.auth?.role))
  const seesAllCenters = activeRoleCode === Role.FINANCE || activeRoleCode === Role.PROCUREMENT_MANAGER
  const centerOptions = useMemo(() => {
    const managed = (authCenterIds || []).map(String)
    const scoped = !seesAllCenters && managed.length > 0
    return (centersPage?.items || [])
      .filter((c) => !scoped || managed.includes(String(c.centerId)))
      .map((c) => ({
        value: c.centerId,
        label: c.sunCenterCode ? `${c.centerName} (${c.sunCenterCode})` : c.centerName,
      }))
  }, [centersPage, authCenterIds, seesAllCenters])

  const { data: departmentsRaw } = useGetDepartmentsQuery()
  const departmentOptions = useMemo(
    () =>
      departmentList(departmentsRaw).map((d) => ({
        value: d.teamId ?? d.id,
        label: d.teamName || d.name || d.teamCode,
      })),
    [departmentsRaw]
  )

  const { data: items, isFetching: itemsLoading } = useGetItemsQuery(
    { categoryId: value.budgetCategoryId || undefined, mustHavePurchase: false, size: 10000 },
    { skip: !value.budgetCategoryId }
  )
  const itemOptions = useMemo(
    () => [
      { value: CUSTOM_ITEM_VALUE, label: "+ Add an item not in this list…" },
      ...(items || []).map((it) => ({ value: it.id, label: it.name || it.itemCode })),
    ],
    [items]
  )

  const updateLine = (idx, linePatch) =>
    patch({ lines: value.lines.map((l, i) => (i === idx ? { ...l, ...linePatch } : l)) })

  const pickItem = (idx, v) => {
    if (v === CUSTOM_ITEM_VALUE) {
      updateLine(idx, {
        itemId: null,
        itemName: "",
        isCustomItem: true,
        customItemName: "",
        offCatalogUnit: "",
        offCatalogDescription: "",
      })
      return
    }
    const opt = itemOptions.find((o) => String(o.value) === String(v))
    updateLine(idx, { itemId: opt?.value ?? null, itemName: opt?.label || "", isCustomItem: false, customItemName: "" })
  }

  const total = nonPoSpendTotal(value.lines)

  return (
    <div className="pc-root" style={{ minHeight: "auto", background: "transparent" }}>
      <Card title="Direct spend details" subtitle="Not linked to a purchase order or a supplier.">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          <Input
            label="Title"
            required
            placeholder="e.g. Team offsite meeting room"
            value={value.title}
            onChange={(e) => patch({ title: e.target.value })}
            disabled={disabled}
          />
          <Select
            label="Budget Category"
            required
            value={value.budgetCategoryId}
            onChange={(v) => patch({ budgetCategoryId: v, lines: value.lines.map((l) => ({ ...l, itemId: null, itemName: "" })) })}
            options={categoryOptions}
            disabled={disabled || categoriesLoading}
            placeholder={categoriesLoading ? "Loading…" : "Select category…"}
            hint="Only Meeting & Entertainment and Travel & Accommodations can be paid without a PO."
          />
          <Select label="Region" value={value.region} onChange={(v) => patch({ region: v })} options={REGION_OPTIONS} disabled={disabled} />
          <Select
            label="Department"
            value={value.departmentId}
            onChange={(v) => patch({ departmentId: v })}
            options={departmentOptions}
            disabled={disabled}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <Textarea
            label="Reason"
            placeholder="Why is this payment needed?"
            value={value.reason}
            onChange={(e) => patch({ reason: e.target.value })}
            disabled={disabled}
          />
        </div>
      </Card>

      <Card
        title="Line items"
        subtitle="At least 1 line. The lines total is the amount of this payment and what is committed against the budget."
        action={
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus aria-hidden />}
            onClick={() => patch({ lines: [...value.lines, newNonPoLine()] })}
            disabled={disabled}
          >
            Add line
          </Button>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {value.lines.map((line, idx) => (
            <div key={line.key}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr)) auto", gap: 16, alignItems: "end" }}>
                <SearchSelect
                  label={`Center (line ${idx + 1})`}
                  required
                  value={line.centerId}
                  onChange={(v) => updateLine(idx, { centerId: v || "" })}
                  options={centerOptions}
                  disabled={disabled || centerOptions.length === 0}
                  placeholder="Select center…"
                />
                {line.isCustomItem ? (
                  <Input
                    label="Item"
                    required
                    placeholder="Item name"
                    value={line.customItemName}
                    onChange={(e) => updateLine(idx, { customItemName: e.target.value })}
                    disabled={disabled}
                    hint={
                      <button
                        type="button"
                        className="prc-linkish"
                        onClick={() =>
                          updateLine(idx, { isCustomItem: false, customItemName: "", offCatalogUnit: "", offCatalogDescription: "" })
                        }
                      >
                        Back to catalog
                      </button>
                    }
                  />
                ) : (
                  <Select
                    label="Item"
                    required
                    value={line.itemId == null ? "" : line.itemId}
                    onChange={(v) => pickItem(idx, v)}
                    options={itemOptions}
                    disabled={disabled || !value.budgetCategoryId || itemsLoading}
                    placeholder={!value.budgetCategoryId ? "Choose a category first" : itemsLoading ? "Loading…" : "Select item…"}
                  />
                )}
                <NumberInput
                  label="Amount"
                  required
                  inputMode="numeric"
                  value={line.amount}
                  disabled={disabled}
                  onChange={(v) => updateLine(idx, { amount: String(v).replace(/[^\d]/g, "") })}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Remove line ${idx + 1}`}
                  leftIcon={<Trash2 aria-hidden />}
                  onClick={() => patch({ lines: value.lines.filter((_, i) => i !== idx) })}
                  disabled={disabled || value.lines.length <= 1}
                />
              </div>
              {line.isCustomItem && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginTop: 10 }}>
                  <Input
                    label="Unit"
                    placeholder="e.g. night, person, session"
                    value={line.offCatalogUnit}
                    onChange={(e) => updateLine(idx, { offCatalogUnit: e.target.value })}
                    disabled={disabled}
                  />
                  <Input
                    label="Description"
                    placeholder="What is this item?"
                    value={line.offCatalogDescription}
                    onChange={(e) => updateLine(idx, { offCatalogDescription: e.target.value })}
                    disabled={disabled}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            alignItems: "baseline",
            marginTop: 12,
            paddingTop: 12,
            borderTop: "1px solid var(--pc-border)",
          }}
        >
          <span className="pc-stat-label">Lines total</span>
          <Currency value={total} strong />
        </div>
      </Card>
    </div>
  )
}

export default NonPoPaymentBuilder
